"use client";

import { useMemo, useRef, useState } from "react";
import "@/styles/sections.css";
import {
  roadmapFilters,
  roadmapMilestones,
  roadmapPhases,
  type RoadmapFilterId,
  type RoadmapMilestone,
  type RoadmapStatus,
} from "@/data/RoadmapMilestoneData";
import { useLanguage } from "@/utils/LanguageContext";
import { useScrollFade } from "@/lib/MotionProvider";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";

const CURRENT_STATUSES: RoadmapStatus[] = ["Ongoing", "In progress"];
const UPCOMING_STATUSES: RoadmapStatus[] = ["Planned", "Pending"];

const matchesFilter = (
  milestone: RoadmapMilestone,
  filter: RoadmapFilterId
): boolean => {
  if (filter === "all") return true;
  if (filter === "completed") return milestone.status === "Completed";
  if (filter === "current") return CURRENT_STATUSES.includes(milestone.status);
  if (filter === "upcoming") return UPCOMING_STATUSES.includes(milestone.status);
  return true;
};

const statusClassName = (status: RoadmapStatus) =>
  `roadmap-status roadmap-status--${status.toLowerCase().replace(/\s+/g, "-")}`;

const RoadmapMilestoneItem = ({
  item,
}: {
  item: RoadmapMilestone;
}) => {
  const { t } = useLanguage();

  return (
    <li className="roadmap-milestone">
      <div className="roadmap-milestone-rail" aria-hidden="true" />
      <div className="roadmap-milestone-dot" aria-hidden="true" />
      <div className="roadmap-milestone-surface">
        <div className="roadmap-milestone-meta">
          <span className="roadmap-time">{t(item.timing)}</span>
          <span className={statusClassName(item.status)}>{t(item.status)}</span>
        </div>
        <h3 className="roadmap-milestone-title">{t(item.title)}</h3>
        {item.note ? (
          <p className="roadmap-milestone-note">{t(item.note)}</p>
        ) : null}
      </div>
    </li>
  );
};

const RoadmapPhaseCard = ({
  label,
  summary,
  items,
  index,
}: {
  label: string;
  summary: string;
  items: RoadmapMilestone[];
  index: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();
  useScrollFade(ref, { direction: index % 2 === 0 ? "left" : "right" });

  return (
    <article ref={ref} className="roadmap-phase-card">
      <div className="roadmap-phase-head">
        <span className="roadmap-phase-index">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div>
          <p className="roadmap-phase-label">{t(label)}</p>
          <p className="roadmap-phase-summary">{t(summary)}</p>
        </div>
      </div>
      <ol className="roadmap-milestone-list">
        {items.map((item) => (
          <RoadmapMilestoneItem key={item.id} item={item} />
        ))}
      </ol>
    </article>
  );
};

const RoadmapArea = () => {
  const { t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState<RoadmapFilterId>("all");
  const introRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);

  useScrollFade(introRef, { direction: "left" });
  useScrollFade(focusRef, { direction: "right" });

  const currentFocusItems = useMemo(
    () => roadmapMilestones.filter((item) => item.currentFocus),
    []
  );

  const groupedPhases = useMemo(
    () =>
      roadmapPhases
        .map((phase) => ({
          ...phase,
          items: roadmapMilestones.filter(
            (item) => item.phase === phase.id && matchesFilter(item, activeFilter)
          ),
        }))
        .filter((phase) => phase.items.length > 0),
    [activeFilter]
  );

  const phaseColumns = useMemo(
    () =>
      groupedPhases.reduce<
        Array<
          Array<{
            index: number;
            phase: (typeof groupedPhases)[number];
          }>
        >
      >(
        (columns, phase, index) => {
          columns[index % 2].push({ index, phase });
          return columns;
        },
        [[], []]
      ),
    [groupedPhases]
  );

  return (
    <section
      className="roadmap-area pt-130 pb-140 overflow-hidden position-relative z-index-common home-anchor-target"
      id="roadmap"
      aria-labelledby="roadmap-title"
    >
      <div className="container">
        <div className="roadmap-shell">
          <div ref={introRef} className="roadmap-copy">
            <div className="section-title mb-0">
              <h2 className="title style2" id="roadmap-title">{t("Roadmap")}</h2>
              <p className="sec-text">
                {t(
                  "A phased view of how Invtron moves from foundational work to production launch and ecosystem scale."
                )}
              </p>
            </div>

            <div
              className="roadmap-filters"
              role="toolbar"
              aria-label={t("Roadmap filters")}
            >
              {roadmapFilters.map((filter) => {
                const active = filter.id === activeFilter;

                return (
                  <button
                    key={filter.id}
                    type="button"
                    className={`roadmap-filter${active ? " is-active" : ""}`}
                    aria-pressed={active}
                    onClick={() => setActiveFilter(filter.id)}
                  >
                    {t(filter.label)}
                  </button>
                );
              })}
            </div>
          </div>

          <aside ref={focusRef} className="roadmap-focus-card">
            <p className="roadmap-focus-label">{t("Current focus")}</p>
            <p className="roadmap-focus-copy">
              {t(
                "The active 2026 workstreams are centered on the token sale, exchange expansion, and production readiness."
              )}
            </p>
            <ul className="roadmap-focus-list">
              {currentFocusItems.map((item) => (
                <li key={item.id} className="roadmap-focus-item">
                  <span className="roadmap-focus-time">{t(item.timing)}</span>
                  <span className="roadmap-focus-title">{t(item.title)}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="roadmap-phase-grid roadmap-phase-grid--desktop">
          {phaseColumns.map((column, columnIndex) => (
            <div
              key={`roadmap-column-${columnIndex + 1}`}
              className="roadmap-phase-column"
            >
              {column.map(({ phase, index }) => (
                <RoadmapPhaseCard
                  key={phase.id}
                  label={phase.label}
                  summary={phase.summary}
                  items={phase.items}
                  index={index}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="roadmap-phase-grid roadmap-phase-grid--mobile">
          {groupedPhases.map((phase, index) => (
            <RoadmapPhaseCard
              key={phase.id}
              label={phase.label}
              summary={phase.summary}
              items={phase.items}
              index={index}
            />
          ))}
        </div>
        <GetInvolvedButton />
      </div>
    </section>
  );
};

export default RoadmapArea;
